/**
 * <menu-drawer> - mobil navigation (THEME-SPEC 1, 2.6). Samme overlay-kontrakt som
 * cart-drawer/search-overlay, men UDEN Section Rendering: menuen er statisk
 * server-renderet (THEME-SPEC 1), saa der hentes intet ved runtime. Komponentens
 * eneste ansvar er overlay-mekanikken: aabne/lukke, focus-retur, aria.
 *
 * Progressive enhancement: uden JS er hamburgeren skjult (kun synlig naar den faar
 * mening), og nav-links/konto vises inline i headeren < 768px-fallback. <dialog>.
 * showModal() giver focus-trap/Escape/inert baggrund nativt; undermenuer bruger
 * native <details> (virker uden JS). Vi laegger focus-retur + aria-expanded oven paa.
 */

class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-menu-dialog]');
    this.opener = null;
    this.back = this.querySelector('[data-menu-back]');
    this.backLabel = this.querySelector('[data-menu-back-label]');
    this.toggle = this.querySelector('gender-toggle');
    this.groups = Array.from(this.querySelectorAll('.menu-drawer__group'));

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => {
      this.#setExpanded(false);
      this.#collapseGroups();
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    });
    this.addEventListener('click', (event) => {
      // GenderToggle navigerer IKKE: gender-toggle.js preventDefault'er og
      // flipper html[data-gender], hvorefter base.css' [data-audience]-regel
      // skifter kategorilisten under os. Drawer'en skal blive staaende - at
      // lukke den ville skjule selve resultatet af skiftet.
      if (event.target.closest('[data-gender-choice]')) return;
      if (event.target.closest('[data-menu-close]')) this.close();
      // En navigation lukker drawer'en: linket foerer til en ny side, men i
      // editor/SPA-agtige tilfaelde sikrer det at overlayet ikke bliver haengende.
      else if (event.target.closest('a[href]')) this.close();
    });

    this.#enhanceLevels();

    MenuDrawer.instance = this;
    if (!MenuDrawer.documentBound) {
      document.addEventListener('click', MenuDrawer.onTriggerClick);
      MenuDrawer.documentBound = true;
    }
  }

  open(opener) {
    if (opener) this.opener = opener;
    if (!this.dialog.open) this.dialog.showModal();
    this.#setExpanded(true);
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  #setExpanded(expanded) {
    document.querySelectorAll('[data-menu-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  // Nulstil aabne undergrupper, saa drawer'en starter rent ved naeste aabning.
  // Tophovedet foelger med tilbage til rodtilstanden.
  #collapseGroups() {
    this.querySelectorAll('details[open]').forEach((group) => {
      group.open = false;
    });
    this.#restoreHeader();
  }

  /**
   * Opgradering: undermenuer bliver paneler i stedet for accordion.
   *
   * Vi styrer <details>.open SELV frem for at lytte paa 'toggle', fordi
   * toggle-eventet fyres asynkront - saa kan fokus og tophoved ikke naa at
   * foelge med i samme tick, og en programmatisk nulstilling (drawer lukkes)
   * kan ikke skelnes fra et brugerklik. Klik paa <summary> er derimod
   * synkront, og Enter/Mellemrum paa en <summary> udloeser ogsaa et klik,
   * saa tastaturet er daekket af samme gren.
   *
   * Uden JS koerer intet af det her: <details> beholder sin native
   * accordion-opfoersel, og CSS'ens panel-regler er gated paa klassen.
   */
  #enhanceLevels() {
    if (!this.groups.length || !this.back || !this.backLabel) return;
    this.classList.add('menu-drawer--enhanced');

    this.addEventListener('click', (event) => {
      const summary = event.target.closest('.menu-drawer__summary');
      if (!summary) return;
      event.preventDefault(); // ellers toggler <details> ogsaa selv
      this.#openPanel(summary.closest('.menu-drawer__group'));
    });

    this.back.addEventListener('click', () => this.#closePanel());

    // Escape gaar ÉT niveau tilbage (ejerbeslutning). Paa rodniveau roerer vi
    // ikke ved noget, saa <dialog> lukker selv som enhver anden modal.
    this.dialog.addEventListener('cancel', (event) => {
      if (!this.#openGroup()) return;
      event.preventDefault();
      this.#closePanel();
    });
  }

  #openGroup() {
    return this.groups.find((group) => group.open) || null;
  }

  #openPanel(group) {
    if (!group || group.open) return;
    // Kun ét niveau ad gangen: ellers ville to paneler ligge oven paa
    // hinanden, og tilbage-knappen vidste ikke hvilket den hoerte til.
    this.groups.forEach((other) => {
      if (other !== group) other.open = false;
    });
    group.open = true;

    const title = group.querySelector('[data-menu-title]');
    this.backLabel.textContent = title ? title.textContent.trim() : '';
    this.back.hidden = false;
    if (this.toggle) this.toggle.hidden = true;
    // Fokus til tilbage-knappen: den er den foerste kontrol i den nye
    // kontekst, og dens navn ("Back to Menswear") fortaeller skaermlaeseren
    // baade at niveauet skiftede og hvorhen.
    this.back.focus();
  }

  #closePanel() {
    const group = this.#openGroup();
    if (!group) return;
    group.open = false;
    this.#restoreHeader();
    // Fokus tilbage paa den raekke man kom fra - ikke ud i det blaa.
    const summary = group.querySelector('.menu-drawer__summary');
    if (summary) summary.focus();
  }

  #restoreHeader() {
    if (!this.back) return;
    this.back.hidden = true;
    this.backLabel.textContent = '';
    if (this.toggle) this.toggle.hidden = false;
  }

  static onTriggerClick(event) {
    const trigger = event.target.closest('[data-menu-trigger]');
    if (!trigger) return;
    const drawer = MenuDrawer.instance;
    if (!drawer) return; // ingen enhancement-target -> trigger goer intet (skjult uden JS)
    event.preventDefault();
    drawer.open(trigger);
  }
}

if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer', MenuDrawer);
}
